import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../services/api';

const AIChat = ({ websiteId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your AI assistant. Ask me anything about your citation data, like:\n\n• What's my brand score for 'resume builder'?\n• Which domains cite us most?\n• Show me keywords with low brand presence\n• How are we performing vs competitors?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await api.post('/ai/chat', {
        message: userMessage,
        websiteId,
        conversationHistory: messages.slice(-10) // Send last 10 messages for context
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        data: response.data.data
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        error: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatData = (data) => {
    if (!data || data.length === 0) return null;

    return (
      <div className="mt-2 rounded-md border bg-muted/30 p-3">
        <div className="text-xs font-semibold mb-2 text-muted-foreground">Data Results:</div>
        <div className="space-y-1 text-sm">
          {data.slice(0, 5).map((item, idx) => (
            <div key={idx} className="font-mono text-xs">
              {Object.entries(item).map(([key, value]) => (
                <span key={key} className="mr-3">
                  <span className="text-muted-foreground">{key}:</span>{' '}
                  <span className="font-semibold">{value}</span>
                </span>
              ))}
            </div>
          ))}
          {data.length > 5 && (
            <div className="text-xs text-muted-foreground italic">
              ...and {data.length - 5} more
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
          <div className="bg-white border shadow-md rounded-full px-3 py-1.5 text-xs font-medium text-foreground hidden sm:inline-block animate-in fade-in slide-in-from-right-2 duration-500">
            Ask anything about your data →
          </div>
          <Button
            onClick={() => setIsOpen(true)}
            className="h-14 px-5 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 gap-2"
          >
            <Sparkles className="h-5 w-5" />
            <span className="font-semibold">Ask AI</span>
          </Button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-[400px] h-[600px] z-50 animate-in slide-in-from-bottom-4 duration-300">
          <Card className="h-full flex flex-col shadow-2xl">
            <CardHeader className="pb-3 border-b bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  <div>
                    <CardTitle className="text-base">AI Assistant</CardTitle>
                    <CardDescription className="text-xs text-blue-100">
                      Ask questions about your data
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8 text-white hover:bg-white/20"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="flex-1 p-0 overflow-hidden flex flex-col">
              {/* Messages */}
              <div className="flex-1 p-4 overflow-y-auto" ref={scrollRef}>
                <div className="space-y-4">
                  {messages.map((message, idx) => (
                    <div
                      key={idx}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-lg px-4 py-2 ${
                          message.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : message.error
                            ? 'bg-red-50 text-red-900 border border-red-200'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                        {message.data && formatData(message.data)}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-lg px-4 py-2 flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">Thinking...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Input */}
              <form onSubmit={handleSubmit} className="p-4 border-t bg-muted/30">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask a question..."
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default AIChat;
